"use client";

import type {
  FocusEvent,
  MouseEvent,
  PointerEvent,
  ReactNode,
  Ref,
} from "react";
import type {
  AdvancedCanvasDropIndicator,
  AdvancedCanvasLegacyDataAttributes,
  AdvancedCanvasPositionStyle,
  AdvancedCanvasRect,
  AdvancedCanvasResizeHandle,
  AdvancedCanvasSlotOverlay,
} from "./types";

export type AdvancedCanvasSlotOverlaysProps = {
  legacyDataAttributes?: AdvancedCanvasLegacyDataAttributes;
  overlays: readonly AdvancedCanvasSlotOverlay[];
};

export function AdvancedCanvasSlotOverlays({
  legacyDataAttributes,
  overlays,
}: AdvancedCanvasSlotOverlaysProps) {
  return overlays.map((overlay) => (
    <div
      key={overlay.key}
      className={[
        "pointer-events-none absolute z-[82] transition-all",
        overlay.kind === "spacer"
          ? "rounded-b-lg border border-dashed border-[#00D7FF]/30 bg-[#00D7FF]/[0.055]"
          : overlay.kind === "row"
            ? "rounded-full bg-[#00D7FF] shadow-[0_0_0_3px_rgba(0,215,255,0.24)]"
            : overlay.kind === "grid"
              ? "rounded-[5px] border bg-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_1px_1px_rgba(15,23,42,0.04)]"
              : "border-2 border-dashed",
        overlay.kind === "spacer"
          ? ""
          : overlay.kind === "slot"
            ? "rounded-2xl"
            : overlay.kind === "grid" || overlay.kind === "row"
              ? ""
              : "rounded-xl",
        overlay.kind === "spacer"
          ? ""
          : overlay.active
            ? overlay.kind === "row"
              ? "bg-[#00D7FF] shadow-[0_0_0_5px_rgba(0,215,255,0.28)]"
              : overlay.kind === "grid"
                ? "border-[#00D7FF] bg-transparent shadow-[0_0_0_3px_rgba(0,215,255,0.24)]"
                : "border-[#00D7FF] shadow-[0_0_0_6px_rgba(0,215,255,0.2)]"
            : overlay.blocked
              ? overlay.kind === "grid"
                ? "border-[#cfcfcf]/60 bg-white/10 opacity-45"
                : "border-rose-400/45 shadow-[0_0_0_3px_rgba(244,63,94,0.08)]"
            : overlay.kind === "row"
                ? "opacity-80"
                : overlay.kind === "slot" || overlay.kind === "grid"
                  ? overlay.kind === "grid"
                    ? "border-[#cfcfcf]"
                    : "border-[#00D7FF]/80 shadow-[0_0_0_5px_rgba(0,215,255,0.16)]"
                  : "border-[#00D7FF]/60 shadow-[0_0_0_4px_rgba(0,215,255,0.12)]",
      ].join(" ")}
      {...legacyDataAttributes}
      data-builder-editor-ui="true"
      data-builder-drop-overlay={overlay.kind}
      data-builder-drop-overlay-active={overlay.active ? "true" : "false"}
      data-builder-drop-overlay-blocked={overlay.blocked ? "true" : "false"}
      style={{
        ...(overlay.kind === "spacer"
          ? {
              backgroundColor: "rgba(0, 215, 255, 0.055)",
              borderColor: "rgba(0, 215, 255, 0.3)",
              borderStyle: "dashed",
              borderWidth: 1,
            }
          : overlay.kind === "grid"
            ? {
                backgroundColor: overlay.active
                  ? "transparent"
                  : overlay.blocked
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(255, 255, 255, 0.28)",
                borderColor: overlay.active
                  ? "#00D7FF"
                  : "rgba(207, 207, 207, 0.92)",
                borderWidth: overlay.active ? 3 : 1,
              }
            : overlay.kind === "row"
              ? {
                  backgroundColor: "#00D7FF",
                }
              : {
                  backgroundColor: overlay.active
                      ? "rgba(20, 184, 166, 0.16)"
                      : overlay.blocked
                        ? "rgba(244, 63, 94, 0.07)"
                        : "rgba(20, 184, 166, 0.07)",
                }),
        height: overlay.height,
        left: overlay.left,
        top: overlay.top,
        width: overlay.width,
      }}
    >
      {overlay.kind !== "slot"
      && overlay.kind !== "grid"
      && overlay.kind !== "row"
      && overlay.kind !== "spacer" ? (
        <span
          className={[
            "absolute left-1/2 h-1.5 w-16 -translate-x-1/2 rounded-full",
            overlay.kind === "above" ? "-top-1" : "-bottom-1",
            overlay.active ? "bg-[#00D7FF]" : "bg-[#00D7FF]/70",
          ].join(" ")}
        />
      ) : null}
      {overlay.kind === "grid"
      || overlay.kind === "row"
      || overlay.kind === "spacer" ? null : (
        <span
          className={[
            "absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-white",
            overlay.active
              ? "bg-[#00D7FF]"
              : overlay.blocked
                ? "bg-rose-400/80"
                : "bg-[#00D7FF]/70",
          ].join(" ")}
        />
      )}
    </div>
  ));
}

export type AdvancedCanvasHoverOutlineProps = {
  outlineRef?: Ref<HTMLDivElement>;
};

/**
 * A single editor-owned hover layer avoids applying outline styles to every
 * rendered content block. BuilderStudio positions it imperatively so dense
 * nested templates do not trigger selector invalidation or full-canvas paint.
 */
export function AdvancedCanvasHoverOutline({
  outlineRef,
}: AdvancedCanvasHoverOutlineProps) {
  return (
    <div
      ref={outlineRef}
      className="pointer-events-none absolute z-[80] border-2 border-[rgba(0,205,255,0.76)]"
      data-builder-editor-ui="true"
      data-builder-hover-outline="true"
      style={{ display: "none" }}
    />
  );
}

export type AdvancedCanvasSelectionOutlineProps = {
  handles: readonly AdvancedCanvasResizeHandle[];
  legacyDataAttributes?: AdvancedCanvasLegacyDataAttributes;
  onResizeStart: (
    handle: AdvancedCanvasResizeHandle,
    event: PointerEvent<HTMLButtonElement>,
  ) => void;
  outset?: number;
  rect: AdvancedCanvasRect;
  resizeLabel: string;
};

export function AdvancedCanvasSelectionOutline({
  handles,
  legacyDataAttributes,
  onResizeStart,
  outset = 8,
  rect,
  resizeLabel,
}: AdvancedCanvasSelectionOutlineProps) {
  return (
    <div
      className="pointer-events-none absolute z-[83] rounded-[10px] border-2 border-[#00D7FF] bg-[#00D7FF]/[0.015] shadow-[0_0_0_1px_rgba(255,255,255,0.86),0_0_0_6px_rgba(0,215,255,0.15),0_16px_38px_rgba(0,99,140,0.16)]"
      {...legacyDataAttributes}
      data-builder-editor-ui="true"
      data-builder-selection-outline="true"
      style={{
        height: rect.height + (outset * 2),
        left: rect.left - outset,
        top: rect.top - outset,
        width: rect.width + (outset * 2),
      }}
    >
      {handles.map((handle) => (
        <button
          key={handle}
          type="button"
          className={[
            "pointer-events-auto absolute h-3 w-3 rounded-[4px] border-2 border-[#00D7FF] bg-white shadow-[0_3px_10px_rgba(15,23,42,0.3)] transition-transform hover:scale-125 hover:bg-cyan-50 touch-none",
            handle === "n"
              ? "left-1/2 -top-1.5 -translate-x-1/2 cursor-n-resize"
              : "",
            handle === "e"
              ? "-right-1.5 top-1/2 -translate-y-1/2 cursor-e-resize"
              : "",
            handle === "s"
              ? "-bottom-1.5 left-1/2 -translate-x-1/2 cursor-s-resize"
              : "",
            handle === "w"
              ? "-left-1.5 top-1/2 -translate-y-1/2 cursor-w-resize"
              : "",
          ].join(" ")}
          {...legacyDataAttributes}
          data-builder-editor-ui="true"
          data-builder-resize-handle={handle}
          aria-label={resizeLabel}
          title={resizeLabel}
          onPointerDown={(event) => onResizeStart(handle, event)}
        />
      ))}
    </div>
  );
}

export type AdvancedFloatingToolbarFrameProps = {
  children: ReactNode;
  className?: string;
  legacyDataAttributes?: AdvancedCanvasLegacyDataAttributes;
  mode: "block" | "endpoint" | "inline-text";
  onBlurCapture?: (event: FocusEvent<HTMLDivElement>) => void;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
  onPointerDownCapture?: (event: PointerEvent<HTMLDivElement>) => void;
  position: AdvancedCanvasPositionStyle;
  toolbarRef?: Ref<HTMLDivElement>;
};

export function AdvancedFloatingToolbarFrame({
  children,
  className = "",
  legacyDataAttributes,
  mode,
  onClick,
  onBlurCapture,
  onMouseDown,
  onPointerDownCapture,
  position,
  toolbarRef,
}: AdvancedFloatingToolbarFrameProps) {
  const baseClassName = mode === "inline-text"
    ? "pointer-events-auto absolute z-[127] flex w-[min(880px,calc(100%-1rem))] flex-wrap items-center gap-1 rounded-lg border border-[#30323d] bg-[#171820] p-1.5 text-slate-100 shadow-[0_16px_42px_rgba(0,0,0,0.36)]"
    : mode === "block"
      ? "pointer-events-auto absolute z-[125] flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-lg border border-[#30323d] bg-[#171820] p-1 text-slate-100 shadow-[0_14px_38px_rgba(0,0,0,0.34)]"
      : "pointer-events-auto absolute z-[126] rounded-lg border border-[#30323d] bg-[#171820] text-slate-100 shadow-[0_16px_42px_rgba(0,0,0,0.38)]";

  return (
    <div
      ref={toolbarRef}
      className={[baseClassName, className].filter(Boolean).join(" ")}
      {...legacyDataAttributes}
      data-builder-editor-ui="true"
      data-builder-floating-toolbar={mode}
      style={position}
      onBlurCapture={onBlurCapture}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onPointerDownCapture={onPointerDownCapture}
    >
      {children}
    </div>
  );
}

export type AdvancedCanvasDropIndicatorLayerProps = {
  indicator: AdvancedCanvasDropIndicator;
  legacyDataAttributes?: AdvancedCanvasLegacyDataAttributes;
};

export function AdvancedCanvasDropIndicatorLayer({
  indicator,
  legacyDataAttributes,
}: AdvancedCanvasDropIndicatorLayerProps) {
  return (
    <div
      className={[
        "pointer-events-none absolute z-[84]",
        indicator.mode === "box"
          ? "rounded-[5px] border-[3px] border-[#00D7FF] bg-transparent shadow-[0_0_0_3px_rgba(0,215,255,0.22)]"
          : "h-1 rounded-full bg-[#00D7FF] shadow-[0_0_0_4px_rgba(0,215,255,0.22)]",
      ].join(" ")}
      {...legacyDataAttributes}
      data-builder-editor-ui="true"
      data-builder-drop-indicator={indicator.mode}
      data-builder-drop-indicator-blocked={indicator.blocked ? "true" : "false"}
      style={{
        height: indicator.height,
        left: indicator.left,
        top: indicator.top,
        width: indicator.width,
      }}
    >
      {indicator.mode === "line" ? (
        <>
          <span className="absolute -left-1 -top-1 h-3 w-3 rounded-full bg-[#00D7FF]" />
          <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[#00D7FF]" />
        </>
      ) : null}
    </div>
  );
}
